import ApiHandler from '@/api/base'
import { UNSELECTED_DATE } from '@/utils/date'
import { UNSELECTED_INTERVAL } from '@/utils/interval'

import AppAbortController from './abort-controller'

const abortController = new AppAbortController()

class HomePageApiHandler extends ApiHandler {
  async getUsersAndGroups({
    searchString = null,
    workspaceId,
    pickedGroupId = null,
    pickedAccountId = null
  }) {
    const { data } = await this.http.get('/homepage/usersAndGroups', {
      params: {
        searchString,
        workspaceId,
        pickedAccountId,
        pickedGroupId
      }
    })
    return data
  }

  async getChartDataForUser({
    accountId,
    workspaceId,
    searchTypeId,
    intervalIds = UNSELECTED_INTERVAL,
    dueDateTo = UNSELECTED_DATE,
    startDateFrom = UNSELECTED_DATE
  }) {
    const { data } = await this.http.get('/chart/user', {
      params: {
        accountId,
        workspaceId,
        searchTypeId,
        granulation: null,
        intervalIds,
        dueDateTo,
        startDateFrom
      }
    })
    return data
  }

  async getChartDataForGroup({
    groupId,
    workspaceId,
    searchTypeId,
    intervalIds = UNSELECTED_INTERVAL,
    dueDateTo = UNSELECTED_DATE,
    startDateFrom = UNSELECTED_DATE
  }) {
    const { data } = await this.http.get('/chart/group', {
      params: {
        groupId,
        workspaceId,
        searchTypeId,
        granulation: null,
        intervalIds,
        dueDateTo,
        startDateFrom
      }
    })
    return data
  }

  async getGroupData({
    groupId,
    workspaceId,
    searchTypeId,
    intervalIds = UNSELECTED_INTERVAL,
    dueDateTo = UNSELECTED_DATE,
    startDateFrom = UNSELECTED_DATE
  }) {
    const { data } = await this.http.get('/homepage/groupData', {
      params: {
        groupId,
        workspaceId,
        searchTypeId,
        intervalIds,
        dueDateTo,
        startDateFrom
      }
    })
    return data
  }

  async getUserData({
    accountId,
    workspaceId,
    searchTypeId,
    intervalIds = UNSELECTED_INTERVAL,
    dueDateTo = UNSELECTED_DATE,
    startDateFrom = UNSELECTED_DATE
  }) {
    const { data } = await this.http.get('/homepage/userData', {
      params: {
        accountId,
        workspaceId,
        searchTypeId,
        intervalIds,
        dueDateTo,
        startDateFrom
      }
    })
    return data
  }

  async getStakeholdersGrade({
    accountId,
    workspaceId,
    groupIds,
    intervalIds = UNSELECTED_INTERVAL,
    dueDateTo = UNSELECTED_DATE,
    startDateFrom = UNSELECTED_DATE
  }) {
    const { data } = await this.http.get('/homepage/getStakeholderGrade', {
      params: {
        accountId,
        workspaceId,
        groupIds,
        intervalIds,
        dueDateTo,
        startDateFrom
      }
    })
    return data
  }

  /**
   * Average progress for the same filters as getElements.
   * avgProgress and weeklyProgressGap are filled only for homepage search types.
   * Returns null when none of the elements have progress.
   */
  async getElementsProgress(payload, { requestId = '' } = {}) {
    const METHOD_NAME = 'getElementsProgress'

    const requestMethodName = requestId ? `${METHOD_NAME}:${requestId}` : METHOD_NAME

    const { data } = await this.http.post('/homepage/getElementsProgress', payload, {
      signal: abortController.getSignal(this.constructor.name, requestMethodName)
    })

    return data
  }

  async getAverageProgress({
    accountId,
    workspaceIds,
    groupId,
    startDateFrom = UNSELECTED_DATE,
    dueDateTo = UNSELECTED_DATE,
    intervalIds = UNSELECTED_INTERVAL,
    gradeTypes
  }) {
    const { data } = await this.http.post(
      '/homepage/progress',
      {
        accountId,
        workspaceIds,
        groupId,
        startDateFrom,
        dueDateTo,
        intervalIds,
        gradeTypes
      },
      {
        signal: abortController.getSignal(this.constructor.name, 'getAverageProgress')
      }
    )
    return data
  }

  async getDashboardByGroupId({ groupId = null, moduleParams = [] } = {}) {
    const { data } = await this.http.post(
      `/homepage/dashboard/${groupId}`,
      { moduleParams },
      { signal: abortController.getSignal(this.constructor.name, 'getDashboardByGroupId') }
    )

    return data
  }

  async getReport({
    groupId = null,
    accountId = null,
    workspaceIds = [],
    intervalIds = null,
    statusIds = []
  } = {}) {
    const { data } = await this.http.get('/homepage/aiReport', {
      params: {
        groupId,
        accountId,
        workspaceIds,
        intervalIds,
        statusIds
      },
      signal: abortController.getSignal(this.constructor.name, 'getReport')
    })

    return data
  }

  // debug: the saved debug settings of the person, left out of the request when null (NFR-1)
  async generate({
    reportTypeId,
    groupId = null,
    accountId = null,
    periodId = null,
    debug = null
  } = {}) {
    const payload = {
      reportTypeId,
      groupId,
      accountId,
      periodId
    }

    if (debug) {
      payload.debug = debug
    }

    const { data } = await this.http.post('/homepage/aiReport/generate', payload)

    return data
  }

  async getAiReportDebugSettings({ groupId = null, accountId = null } = {}) {
    const { data } = await this.http.get('/homepage/aiReport/debug/settings', {
      params: {
        groupId,
        accountId
      }
    })

    return data
  }

  async getAiReportDebugCalls({ groupId = null, accountId = null } = {}) {
    const { data } = await this.http.get('/homepage/aiReport/debug/calls', {
      params: {
        groupId,
        accountId
      }
    })

    return data
  }
}

export default HomePageApiHandler
